import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useTabs, { TabConfig, UseTabsReturn } from '@cas/components/tabs/tabs/useTabs';
import { UiSettingsModel } from '@casops/store/apis/settingsApi';
import { Control, FieldValues, UseFormResetField } from 'react-hook-form';
import LocalizationControls from '../localization/LocalizationControls';
import MapProviderControls from '../map/MapProviderControls';
import { useAppSelector } from '@casops/store/hooks';
import { getFormTabActiveView } from '@casops/side-panels/utils';
import SiteDetailsProviderControls from '../site-details/SiteDetailsProviderControls';

export interface UseSettingsFormTabsProps<TFormValues extends FieldValues> {
  control: Control<TFormValues, unknown, TFormValues>;
  resetField: UseFormResetField<TFormValues>;
}

export const useSettingsFormTabs = ({
  control,
  resetField,
}: UseSettingsFormTabsProps<UiSettingsModel>): UseTabsReturn => {
  const { t } = useTranslation();

  const sidePanelTab = useAppSelector(state => state.global.sidePanelTab);

  const tabConfigs = useMemo<TabConfig[]>(() => {
    return [
      {
        title: t('Navigation.SiteDetails'),
        view: <SiteDetailsProviderControls control={control} />,
        isDotted: false,
      },
      {
        title: t('Navigation.Localization'),
        view: <LocalizationControls control={control} />,
        isDotted: false,
      },
      {
        title: t('Navigation.Map'),
        view: <MapProviderControls control={control} resetField={resetField} />,
        isDotted: false,
      },
    ];
  }, [t, control, resetField]);

  return useTabs({
    tabConfigs,
    defaultTab: sidePanelTab,
    getActiveView: getFormTabActiveView,
  });
};

export default useSettingsFormTabs;
